import { useEffect, useState } from 'react';

import {
  type CardLimitsIncreaseScaOperationOutput,
  postCardLimitsIncreaseScaOperation,
} from 'modules/cards/api/postCardLimitsIncreaseScaOperation';
import type { PostV2ApiCompanyIdCardsIncreaseLimitsProcedureIdRequest } from 'src/client/spendesk-api-types';

export const usePostCardLimitsIncreaseScaOperationStatus = ({
  authenticationMethod,
  companyId,
  procedureId,
  description,
}: {
  authenticationMethod: PostV2ApiCompanyIdCardsIncreaseLimitsProcedureIdRequest['body']['authenticationMethod'];
  companyId: string;
  procedureId: string | undefined;
  description: string;
}) => {
  const [procedureFailedReason, setProcedureFailedReason] = useState<
    'initializationError' | 'unknown' | 'timeout' | 'rejected' | undefined
  >(undefined);
  const [
    cardLimitsIncreaseScaOperationOutput,
    setCardLimitsIncreaseScaOperationOutput,
  ] = useState<undefined | CardLimitsIncreaseScaOperationOutput>(undefined);

  useEffect(() => {
    const handlePostCardLimitsIncreaseScaOperationOutput = async (
      interval?: NodeJS.Timeout,
    ) => {
      if (procedureId) {
        try {
          // Get the SCA operation status
          const response = await postCardLimitsIncreaseScaOperation({
            authenticationMethod,
            companyId,
            procedureId,
            description,
          });

          if (response.procedureStatus === 'approved') {
            setCardLimitsIncreaseScaOperationOutput(response);
            clearInterval(interval);
          }
          if (response.procedureStatus === 'rejected') {
            setProcedureFailedReason('rejected');
            clearInterval(interval);
          }
          if (response.procedureStatus === 'timeout') {
            setProcedureFailedReason('timeout');
            clearInterval(interval);
          }
        } catch {
          setProcedureFailedReason('unknown');
        }
      }
    };

    if (procedureId) {
      if (authenticationMethod === 'sca') {
        const interval = setInterval(
          () => handlePostCardLimitsIncreaseScaOperationOutput(interval),
          2500,
        );
        return () => clearInterval(interval);
      }

      handlePostCardLimitsIncreaseScaOperationOutput();
    }
  }, [procedureId]);

  return {
    procedureFailedReason,
    setProcedureFailedReason,
    cardLimitsIncreaseScaOperationOutput,
  };
};
